import React, { createRef, useRef } from "react";
import Lottie from "lottie-react";
import { FaCheck } from "react-icons/fa";
import { PRODUCT_TYPES, productNames } from "./constants";
import darkTeabrewAnimation from "../../Assets/lottie/white-teabrew.json";
import filtreKahveAnimation from "../../Assets/lottie/filtrekahve-brew-white.json";
import turkishCoffeeAnimation from "../../Assets/lottie/white-turkishcoffeeready.json";
import americanoAnimation from "../../Assets/lottie/Americano Coffee - Caff Americano.json";

// Ürün başına görsel kimlik — hex yerine Tailwind token'ları kullanılır.
const PRODUCT_META = {
  cay: {
    icon: "🫖",
    tagline: "Geleneksel çay lezzeti",
    idleCard: "border-gray-200 bg-white hover:border-emerald-200",
    activeCard: "border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-100",
    iconBg: "bg-emerald-50",
    badge: "bg-emerald-500",
  },
  filtreKahve: {
    icon: "☕️",
    tagline: "Üçüncü nesil filtre deneyimi",
    idleCard: "border-gray-200 bg-white hover:border-orange-200",
    activeCard: "border-orange-500 bg-orange-50/60 ring-2 ring-orange-100",
    iconBg: "bg-orange-50",
    badge: "bg-orange-500",
  },
  turkKahvesi: {
    icon: "🥄",
    tagline: "Fincanda taze pişirme",
    idleCard: "border-gray-200 bg-white hover:border-red-200",
    activeCard: "border-red-500 bg-red-50/60 ring-2 ring-red-100",
    iconBg: "bg-red-50",
    badge: "bg-red-500",
  },
  espresso: {
    icon: "⚡️",
    tagline: "Profesyonel espresso",
    idleCard: "border-gray-200 bg-white hover:border-purple-200",
    activeCard: "border-purple-500 bg-purple-50/60 ring-2 ring-purple-100",
    iconBg: "bg-purple-50",
    badge: "bg-purple-500",
  },
};

const ANIMATIONS = {
  cay: darkTeabrewAnimation,
  filtreKahve: filtreKahveAnimation,
  turkKahvesi: turkishCoffeeAnimation,
  espresso: americanoAnimation,
};

export default function ProductSelectionStep({
  selectedProducts,
  onToggleProduct,
  onContinue,
  hideContinueButton = false,
}) {
  // Her ürün için ayrı Lottie ref'i (lottie-react ref objesi bekler, callback
  // ref'i doldurmaz) — tıklamada animasyon baştan oynatılır.
  const lottieRefs = useRef(
    PRODUCT_TYPES.reduce(
      (acc, productType) => ({ ...acc, [productType]: createRef() }),
      {}
    )
  );

  const handleProductClick = (productType) => {
    const instance = lottieRefs.current[productType]?.current;
    if (instance) {
      instance.setDirection(1);
      instance.play();
    }
    onToggleProduct(productType);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5">
        <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
          Ürün & Anlaşma
        </p>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mt-1">
          Hangi ürün için teklif hazırlamak istersiniz?
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Birden fazla ürünü aynı anda seçebilir, sonraki adımda her biri için
          fiyat ve makine bilgisi verebilirsiniz.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PRODUCT_TYPES.map((productType) => {
          const isActive = selectedProducts.includes(productType);
          const meta = PRODUCT_META[productType] || {};
          const animationData = ANIMATIONS[productType];

          return (
            <button
              key={productType}
              type="button"
              onClick={() => handleProductClick(productType)}
              aria-pressed={isActive}
              className={`relative flex flex-col items-start gap-3 p-4 border-2 rounded-2xl shadow-sm text-left transition-all duration-200 hover:shadow-md ${
                isActive ? meta.activeCard : meta.idleCard
              }`}
            >
              {isActive && (
                <span
                  className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${meta.badge}`}
                >
                  <FaCheck className="w-2.5 h-2.5" />
                  Seçildi
                </span>
              )}

              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden text-2xl ${meta.iconBg}`}
              >
                {animationData ? (
                  <Lottie
                    lottieRef={lottieRefs.current[productType]}
                    animationData={animationData}
                    loop
                    autoplay={false}
                    style={{ width: 48, height: 48 }}
                  />
                ) : (
                  meta.icon || "☕️"
                )}
              </div>

              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {productNames[productType]}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {meta.tagline || "Ürün açıklaması"}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {selectedProducts.length > 0 && !hideContinueButton && onContinue && (
        <div className="mt-6 text-center">
          <button
            onClick={onContinue}
            className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-sm hover:shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
          >
            Devam Et
          </button>
        </div>
      )}
    </div>
  );
}
